import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { FileText, ShieldAlert, CheckCircle2, AlertTriangle, BookOpen, Sliders, Mic, MicOff, Eye, X, Send, Folder } from "lucide-react";

const BAND_COLOUR = {
  H: { bg: "#fef2f2", border: "#dc2626", text: "#991b1b" },
  M: { bg: "#fffbeb", border: "#f59e0b", text: "#92400e" },
  L: { bg: "#ecfdf5", border: "#059669", text: "#065f46" },
};

const TRI = [
  { v: "yes", label: "Yes", colour: "#dc2626" },
  { v: "no", label: "No", colour: "#059669" },
  { v: "unknown", label: "Unknown", colour: "#64748b" },
];

const PEOPLE_LABELS = { O: "Operatives", C: "Client/contractors", P: "Product", V: "Visitors", PU: "Public" };

function RatingChip({ band, L, S, R, testid }) {
  const c = BAND_COLOUR[band] || BAND_COLOUR.L;
  return (
    <span
      data-testid={testid}
      className="mono"
      style={{
        display: "inline-block",
        padding: "3px 8px",
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
        borderRadius: 2,
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {L}×{S}={R} ({band})
    </span>
  );
}

function ControlsList({ text }) {
  const items = (text || "").split(";").map((s) => s.trim()).filter(Boolean);
  if (items.length <= 1) return <div style={{ fontSize: 12, lineHeight: 1.5 }}>{text}</div>;
  return (
    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, lineHeight: 1.55 }}>
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}

function HazardRow({ h, dim, reviewFlagged }) {
  return (
    <tr data-testid={`hazard-row-${h.id}`} style={{ opacity: dim ? 0.45 : 1 }}>
      <td className="mono" style={{ fontWeight: 700, color: "#0f172a", verticalAlign: "top" }}>{h.id}</td>
      <td style={{ verticalAlign: "top" }}>
        <div style={{ fontWeight: 600 }}>{h.name}</div>
        {reviewFlagged ? (
          <div className="mono" style={{ fontSize: 10, color: "#b91c1c", marginTop: 2 }}>
            <AlertTriangle size={10} style={{ verticalAlign: -1, marginRight: 2 }} />
            Review required — flag was Unknown
          </div>
        ) : null}
        {h._inclusion_reason ? (
          <div className="mono" style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>
            {h._inclusion_reason}
          </div>
        ) : null}
      </td>
      <td className="mono" style={{ fontSize: 11, color: "#475569", verticalAlign: "top" }}>
        {(h.persons_at_risk || []).join(", ")}
      </td>
      <td style={{ verticalAlign: "top" }}>
        <RatingChip {...h.initial} testid={`hazard-${h.id}-initial`} />
      </td>
      <td style={{ verticalAlign: "top", minWidth: 280 }}>
        <ControlsList text={h.controls} />
      </td>
      <td style={{ verticalAlign: "top" }}>
        <RatingChip {...h.residual} testid={`hazard-${h.id}-residual`} />
      </td>
    </tr>
  );
}

function LibraryTable({ hazards, reviewIds = [], dimIds = [], testid = "rams-library-table" }) {
  const rSet = new Set(reviewIds);
  const dSet = new Set(dimIds);
  return (
    <div className="card" style={{ padding: 0 }} data-testid={testid}>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Hazard</th>
            <th>At risk</th>
            <th>Initial (L×S=R)</th>
            <th>Control measures</th>
            <th>Residual (L×S=R)</th>
          </tr>
        </thead>
        <tbody>
          {hazards.map((h) => (
            <HazardRow key={h.id} h={h} dim={dSet.has(h.id)} reviewFlagged={rSet.has(h.id)} />
          ))}
          {hazards.length === 0 ? <tr><td colSpan={6} style={{ textAlign: "center", padding: 24, color: "#94a3b8" }}>No hazards selected.</td></tr> : null}
        </tbody>
      </table>
    </div>
  );
}

function LibraryViewer({ lib }) {
  // Split always/conditional
  const always = lib.hazards.filter((h) => h.inclusion?.rule === "always");
  const conditional = lib.hazards.filter((h) => h.inclusion?.rule === "conditional");
  return (
    <div data-testid="rams-library-viewer">
      <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 14 }}>
        Library v{lib.meta?.version} · sourced from <b>{lib.meta?.source_master}</b> (v{lib.meta?.source_master_version}) · generated {lib.meta?.generated}.
        Control text is verbatim from the locked master — it is never AI-generated per job.
      </div>

      <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", margin: "8px 0 8px" }}>
        Always-included hazards ({always.length})
      </h2>
      <LibraryTable hazards={always} testid="library-always-table" />

      <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", margin: "28px 0 8px" }}>
        Conditional hazards ({conditional.length}) — included when their flag is set or unknown
      </h2>
      <LibraryTable hazards={conditional} testid="library-conditional-table" />

      <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", margin: "28px 0 8px" }}>
        Permit triggers ({lib.permit_mapping.length})
      </h2>
      <div className="card" style={{ padding: 0 }} data-testid="library-permits-table">
        <table>
          <thead><tr><th>Permit</th><th>Trigger</th></tr></thead>
          <tbody>
            {lib.permit_mapping.map((p, i) => (
              <tr key={i}><td style={{ fontWeight: 600 }}>{p.permit}</td><td className="mono" style={{ fontSize: 11 }}>{p.trigger}</td></tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", margin: "28px 0 8px" }}>
        Fixed sections — boilerplate from the master ({lib.fixed_sections_from_master?.sections?.length || 0})
      </h2>
      <div className="card" style={{ padding: 16 }} data-testid="library-fixed-sections">
        <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
          {(lib.fixed_sections_from_master?.sections || []).map((s, i) => <li key={i}>{s}</li>)}
        </ol>
        <div className="mono" style={{ fontSize: 11, color: "#64748b", marginTop: 10 }}>
          {lib.fixed_sections_from_master?.note}
        </div>
      </div>

      <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", margin: "28px 0 8px" }}>
        Persons-at-risk key
      </h2>
      <div className="card" style={{ padding: 16 }} data-testid="library-people-key">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 18, fontSize: 13 }}>
          {Object.entries(lib.persons_at_risk_key || {}).map(([k, v]) => (
            <div key={k}><b className="mono" style={{ color: "var(--brand-accent, #F05A14)" }}>{k}</b> &nbsp;— {v}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScopeOfWorks({ value, onChange }) {
  const [listening, setListening] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const recRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setUnsupported(true); return; }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-GB";
    let baseAtStart = "";
    rec.onstart = () => { baseAtStart = value || ""; };
    rec.onresult = (e) => {
      let interim = "";
      let finalAdd = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalAdd += t;
        else interim += t;
      }
      const next = (baseAtStart + (baseAtStart && !baseAtStart.endsWith(" ") ? " " : "") + finalAdd + interim).trimStart();
      onChange(next);
      if (finalAdd) baseAtStart = (baseAtStart + (baseAtStart && !baseAtStart.endsWith(" ") ? " " : "") + finalAdd).trimStart();
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;
    return () => { try { rec.stop(); } catch { /* noop */ } };
  }, []);

  const toggle = () => {
    if (!recRef.current) return;
    if (listening) { try { recRef.current.stop(); } catch { /* noop */ } setListening(false); }
    else { try { recRef.current.start(); setListening(true); } catch { /* noop */ } }
  };

  return (
    <div data-testid="scope-of-works-block">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#475569" }}>
          Scope of works — what is being erected
        </label>
        <button
          type="button"
          data-testid="scope-mic-toggle"
          onClick={toggle}
          disabled={unsupported}
          title={unsupported ? "Voice not supported on this browser" : (listening ? "Stop recording" : "Dictate")}
          className="mono"
          style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "4px 8px", fontSize: 10, fontWeight: 700,
            border: `1px solid ${listening ? "#dc2626" : "#cbd5e1"}`,
            background: listening ? "#dc2626" : "white",
            color: listening ? "white" : (unsupported ? "#94a3b8" : "#475569"),
            borderRadius: 2, cursor: unsupported ? "not-allowed" : "pointer",
          }}
        >
          {listening ? <MicOff size={11} /> : <Mic size={11} />}
          {listening ? "Recording…" : (unsupported ? "Voice n/a" : "Dictate")}
        </button>
      </div>
      <textarea
        data-testid="scope-of-works-input"
        className="input-base"
        rows={4}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Type or dictate the supervisor's narrative — e.g. 'Independent scaffold on east side of fryer line 3, three lifts to roof level, for hood maintenance over two shifts.'"
        style={{ minHeight: 96, resize: "vertical" }}
      />
      <div className="mono" style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>
        {unsupported
          ? "Voice dictation needs Chrome/Edge/Safari with microphone access. Typing still works."
          : "Tap Dictate to add your voice as text. The hazard flags are separate — this is your job narrative."}
      </div>
    </div>
  );
}


function PreviewRAMSModal({ open, onClose, preview, intake, structure, structures, lib }) {
  if (!open || !preview) return null;
  const structureRow = structure || (structures || []).find((s) => s.id === preview.structure_id);
  const reviewSet = new Set(preview.review_required_hazard_ids || []);
  const fmt = (s) => (s == null || s === "" ? "—" : String(s));

  return (
    <div
      data-testid="preview-rams-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 200, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: 16, overflowY: "auto" }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ width: "100%", maxWidth: 980, padding: 0, marginTop: 24, marginBottom: 24 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ background: "#1a1a1a", color: "white", padding: "18px 24px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <div className="mono" style={{ color: "#cbd5e1", fontSize: 10, letterSpacing: "0.14em" }}>RISK ASSESSMENT &amp; METHOD STATEMENT — DRAFT PREVIEW</div>
            <div className="font-chivo" style={{ fontSize: 22, fontWeight: 900, marginTop: 2 }}>
              {structureRow ? structureRow.description : "RAMS preview"}
            </div>
            <div className="mono" style={{ color: "#cbd5e1", fontSize: 11, marginTop: 4 }}>
              Library v{lib?.meta?.version} · master v{lib?.meta?.source_master_version} · {intake?.job_ref || "(no job ref)"}
              {intake?.mccain_po_or_permit_ref ? ` · McCain ref ${intake.mccain_po_or_permit_ref}` : ""}
            </div>
          </div>
          <button data-testid="preview-modal-close" onClick={onClose} className="btn-ghost" style={{ background: "transparent", borderColor: "#475569", color: "#cbd5e1", padding: "6px 10px" }}>
            <X size={14} /> Close
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {/* Intake block */}
          <h3 style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569" }}>1 · Job &amp; intake</h3>
          <div className="card" style={{ padding: 14, marginBottom: 18 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, fontSize: 13 }}>
              <div><span className="mono" style={{ color: "#94a3b8" }}>Structure</span><br /><b>{structureRow ? `${structureRow.description}` : "—"}</b></div>
              <div><span className="mono" style={{ color: "#94a3b8" }}>Area</span><br /><b>{structureRow?.area_name || fmt(intake?.area_or_line)}</b></div>
              <div><span className="mono" style={{ color: "#94a3b8" }}>Job ref</span><br /><b>{fmt(intake?.job_ref)}</b></div>
              <div><span className="mono" style={{ color: "#94a3b8" }}>McCain PO / permit ref</span><br /><b>{fmt(intake?.mccain_po_or_permit_ref)}</b></div>
            </div>
            <div style={{ marginTop: 14 }}>
              <div className="mono" style={{ color: "#94a3b8", fontSize: 11 }}>Scope of works (supervisor narrative)</div>
              <div data-testid="preview-scope-text" style={{ marginTop: 4, lineHeight: 1.55, fontSize: 13, whiteSpace: "pre-wrap" }}>
                {fmt(intake?.scope_of_works)}
              </div>
            </div>
          </div>

          {/* Hazards */}
          <h3 style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569" }}>
            2 · Selected hazards ({(preview.selected_hazards || []).length})
            {preview.review_required_hazard_ids?.length ? <span style={{ color: "#b91c1c", marginLeft: 8 }}>— {preview.review_required_hazard_ids.length} flagged for review</span> : null}
          </h3>
          <LibraryTable hazards={preview.selected_hazards || []} reviewIds={preview.review_required_hazard_ids || []} testid="preview-modal-hazards" />

          {/* Permits */}
          {preview.permits_proposed?.length ? (
            <>
              <h3 style={{ margin: "24px 0 8px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569" }}>
                3 · Permits proposed ({preview.permits_proposed.length})
              </h3>
              <div className="card" style={{ padding: 0 }} data-testid="preview-modal-permits">
                <table>
                  <thead><tr><th>Permit</th><th>Trigger</th></tr></thead>
                  <tbody>
                    {preview.permits_proposed.map((p, i) => (
                      <tr key={i}><td style={{ fontWeight: 600 }}>{p.permit}</td><td className="mono" style={{ fontSize: 11 }}>{p.trigger}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}

          {/* Fixed sections list */}
          {(preview.fixed_sections || []).length ? (
            <>
              <h3 style={{ margin: "24px 0 8px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569" }}>
                4 · Boilerplate sections (verbatim from master)
              </h3>
              <div className="card" style={{ padding: 14 }} data-testid="preview-modal-sections">
                <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7, fontSize: 13 }}>
                  {preview.fixed_sections.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </div>
            </>
          ) : null}

          {/* Footer note */}
          <div className="card" style={{ padding: 14, marginTop: 20, background: "#fffbeb", borderLeft: "4px solid #f59e0b" }} data-testid="preview-modal-footer-note">
            <div className="mono" style={{ fontSize: 11, color: "#92400e", lineHeight: 1.55 }}>
              <b>DRAFT — not signed.</b> Click <b>Generate RAMS &amp; start sign-off</b> below to persist this draft and open the sign-off workflow (Stage 1 → Stage 2 → DRA).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function IntakePreview({ lib }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialStructureTypeId = searchParams.get("structure_type_id") || "";
  const [structureTypeId, setStructureTypeId] = useState(initialStructureTypeId);
  const [structureTypes, setStructureTypes] = useState([]);
  const [intake, setIntake] = useState({});
  const [flags, setFlags] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genErr, setGenErr] = useState("");

  useEffect(() => {
    // Spec §4: RAMS picks a TYPE (catalogue), not an erected structure.
    api.get("/structure-types").then((r) => setStructureTypes(r.data || []));
    const init = {};
    (lib.area_flags || []).forEach((f) => { init[f] = "unknown"; });
    setFlags(init);
  }, [lib]);

  // Live preview as the user toggles
  useEffect(() => {
    if (!lib?.hazards?.length) return;
    setBusy(true);
    api.post("/rams/preview", { structure_type_id: structureTypeId || null, intake, area_flags: flags })
      .then((r) => setPreview(r.data))
      .finally(() => setBusy(false));
  }, [JSON.stringify(flags), structureTypeId, intake.job_ref, intake.area_or_line, intake.mccain_po_or_permit_ref, intake.scope_of_works, lib]);

  const setFlag = (k, v) => setFlags((f) => ({ ...f, [k]: v }));
  const setScope = (v) => setIntake((i) => ({ ...i, scope_of_works: v }));

  const reviewIds = preview?.review_required_hazard_ids || [];
  const pickedStructureType = structureTypes.find((t) => t.id === structureTypeId);
  const canPreview = !!structureTypeId; // require a TYPE choice before previewing

  return (
    <div data-testid="rams-intake-preview" style={{ display: "grid", gridTemplateColumns: "minmax(0, 360px) 1fr", gap: 20 }}>
      {/* Intake panel */}
      <div data-testid="intake-panel" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="card" style={{ padding: 16 }}>
          <h3 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>Job</h3>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#475569" }}>Structure type (catalogue)</label>
          <select data-testid="intake-structure-type" className="input-base" value={structureTypeId} onChange={(e) => setStructureTypeId(e.target.value)}>
            <option value="">— pick a structure type —</option>
            {structureTypes.map((t) => <option key={t.id} value={t.id}>{t.name}{t.description ? ` · ${t.description}` : ""}</option>)}
          </select>
          <p style={{ fontSize: 10, color: "#64748b", marginTop: 4, marginBottom: 0 }}>RAMS is a template per TYPE. When a structure of this type is erected, this RAMS gates its handover.</p>

          {["job_ref", "mccain_po_or_permit_ref", "area_or_line"].map((field) => (
            <div key={field} style={{ marginTop: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#475569" }}>{field.replace(/_/g, " ")}</label>
              <input data-testid={`intake-${field}`} className="input-base" value={intake[field] || ""} onChange={(e) => setIntake((i) => ({ ...i, [field]: e.target.value }))} />
            </div>
          ))}

          <div style={{ marginTop: 14 }}>
            <ScopeOfWorks value={intake.scope_of_works} onChange={setScope} />
          </div>
        </div>

        <div className="card" style={{ padding: 16 }}>
          <h3 style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>Area flags ({(lib.area_flags || []).length})</h3>
          <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>
            Tri-state · Unknown = include &amp; flag for review (fail-safe).
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(lib.area_flags || []).map((f) => (
              <div key={f} data-testid={`flag-row-${f}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, borderBottom: "1px dashed #e2e8f0", paddingBottom: 6 }}>
                <span style={{ fontSize: 12, color: "#0f172a" }}>{f.replace(/_/g, " ")}</span>
                <div style={{ display: "inline-flex", gap: 4 }}>
                  {TRI.map((t) => {
                    const active = (flags[f] || "unknown") === t.v;
                    return (
                      <button
                        key={t.v}
                        type="button"
                        data-testid={`flag-${f}-${t.v}`}
                        onClick={() => setFlag(f, t.v)}
                        className="mono"
                        style={{
                          padding: "3px 8px",
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          border: `1px solid ${active ? t.colour : "#cbd5e1"}`,
                          background: active ? t.colour : "white",
                          color: active ? "white" : "#475569",
                          borderRadius: 2,
                          cursor: "pointer",
                        }}
                      >{t.label}</button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Preview panel */}
      <div data-testid="preview-panel">
        <div className="card" style={{ padding: 14, marginBottom: 14, display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center" }}>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <div data-testid="stat-selected">
              <div className="mono" style={{ fontSize: 10, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em" }}>Hazards selected</div>
              <div className="font-chivo" style={{ fontSize: 26, fontWeight: 900, color: "#0f172a", lineHeight: 1 }}>{preview?.selected_hazards?.length ?? "—"}</div>
            </div>
            <div data-testid="stat-review">
              <div className="mono" style={{ fontSize: 10, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em" }}>Review required</div>
              <div className="font-chivo" style={{ fontSize: 26, fontWeight: 900, color: "#b91c1c", lineHeight: 1 }}>{preview?.review_required_hazard_ids?.length ?? "—"}</div>
            </div>
            <div data-testid="stat-omitted">
              <div className="mono" style={{ fontSize: 10, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em" }}>Omitted (flag = No)</div>
              <div className="font-chivo" style={{ fontSize: 26, fontWeight: 900, color: "#64748b", lineHeight: 1 }}>{preview?.omitted_hazards?.length ?? "—"}</div>
            </div>
            <div data-testid="stat-permits">
              <div className="mono" style={{ fontSize: 10, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em" }}>Permits proposed</div>
              <div className="font-chivo" style={{ fontSize: 26, fontWeight: 900, color: "var(--brand-accent, #F05A14)", lineHeight: 1 }}>{preview?.permits_proposed?.length ?? "—"}</div>
            </div>
          </div>
          {busy ? <div className="mono" style={{ fontSize: 11, color: "#94a3b8" }}>Recomputing…</div> : null}
          <div style={{ marginLeft: "auto", display: "inline-flex", gap: 8, flexWrap: "wrap" }}>
            <button
              data-testid="open-preview-rams"
              type="button"
              disabled={!canPreview}
              onClick={() => setShowModal(true)}
              className="btn-ghost"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, opacity: canPreview ? 1 : 0.55, cursor: canPreview ? "pointer" : "not-allowed" }}
            >
              <Eye size={14} /> Preview
            </button>
            <button
              data-testid="generate-rams"
              type="button"
              disabled={!canPreview || generating}
              onClick={async () => {
                setGenErr(""); setGenerating(true);
                try {
                  const r = await api.post("/rams", { structure_type_id: structureTypeId, intake, area_flags: flags });
                  navigate(`/rams/${r.data.id}`);
                } catch (e) { setGenErr(e.response?.data?.detail || e.message); }
                finally { setGenerating(false); }
              }}
              className="btn-primary"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, opacity: canPreview ? 1 : 0.55, cursor: canPreview ? "pointer" : "not-allowed" }}
              title={canPreview ? "Persist this draft and open the sign-off workflow" : "Pick a structure first"}
            >
              <Send size={14} /> {generating ? "Generating…" : "Generate RAMS & start sign-off"}
            </button>
          </div>
        </div>

        {genErr ? <div className="badge badge-red" style={{ padding: "8px 12px", marginBottom: 12 }} data-testid="generate-err">{genErr}</div> : null}

        {!canPreview ? (
          <div className="card" style={{ padding: 12, borderLeft: "4px solid var(--brand-accent, #F05A14)", marginBottom: 14 }} data-testid="rams-pick-structure-hint">
            <div className="mono" style={{ fontSize: 11, color: "#475569" }}>
              <b>Each structure has its own RAMS.</b> Pick a structure above, then tap <b>Generate RAMS &amp; start sign-off</b> to persist the draft and open the Stage 1 → Stage 2 → DRA workflow.
            </div>
          </div>
        ) : null}

        <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", margin: "0 0 8px" }}>
          Selected hazards
        </h2>
        <LibraryTable
          hazards={preview?.selected_hazards || []}
          reviewIds={reviewIds}
          testid="preview-selected-table"
        />

        {preview?.permits_proposed?.length ? (
          <>
            <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", margin: "24px 0 8px" }}>
              Permits proposed
            </h2>
            <div className="card" style={{ padding: 0 }} data-testid="preview-permits-table">
              <table>
                <thead><tr><th>Permit</th><th>Trigger</th></tr></thead>
                <tbody>
                  {preview.permits_proposed.map((p, i) => (
                    <tr key={i}><td style={{ fontWeight: 600 }}>{p.permit}</td><td className="mono" style={{ fontSize: 11 }}>{p.trigger}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        {preview?.omitted_hazards?.length ? (
          <>
            <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#64748b", margin: "24px 0 8px" }}>
              Explicitly omitted ({preview.omitted_hazards.length}) — flag = No
            </h2>
            <div className="card" style={{ padding: 12 }} data-testid="preview-omitted-list">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7, fontSize: 13, color: "#475569" }}>
                {preview.omitted_hazards.map((o) => (
                  <li key={o.id} data-testid={`omitted-${o.id}`}><b className="mono">{o.id}</b> · {o.name} <span className="mono" style={{ fontSize: 11, color: "#94a3b8" }}>({o.reason})</span></li>
                ))}
              </ul>
            </div>
          </>
        ) : null}
      </div>

      <PreviewRAMSModal
        open={showModal}
        onClose={() => setShowModal(false)}
        preview={preview}
        intake={intake}
        structure={pickedStructureType}
        structures={structureTypes}
        lib={lib}
      />
    </div>
  );
}

function Register() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/rams").then((r) => setItems(r.data || [])).catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);
  const filtered = filter === "All" ? items : items.filter((r) => r.status === filter);
  const STATUS_ORDER = ["All", "Draft", "Stage1Signed", "Stage2Approved", "DRASigned", "Superseded"];
  return (
    <div data-testid="rams-register">
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }} data-testid="register-filters">
        {STATUS_ORDER.map((s) => (
          <button key={s} data-testid={`filter-${s}`} type="button" onClick={() => setFilter(s)} className={filter === s ? "btn-primary" : "btn-ghost"} style={{ padding: "5px 10px", fontSize: 12 }}>
            {s} {s !== "All" ? `(${items.filter((r) => r.status === s).length})` : `(${items.length})`}
          </button>
        ))}
      </div>
      {err ? <div className="badge badge-red" style={{ padding: "8px 12px" }} data-testid="register-err">{err}</div> : null}
      <div className="card" style={{ padding: 0 }} data-testid="register-table">
        <table>
          <thead>
            <tr><th>Structure</th><th>v</th><th>Job ref</th><th>Status</th><th>Prepared by</th><th>Stage 1</th><th>Stage 2</th><th>DRA</th><th>Created</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`register-row-${r.id}`}>
                <td style={{ fontWeight: 600 }}>{r.structure_description}</td>
                <td className="mono">v{r.version}</td>
                <td className="mono" style={{ fontSize: 11 }}>{r.intake?.job_ref || "—"}</td>
                <td>
                  <span data-testid={`row-status-${r.id}`} className="mono" style={{
                    padding: "2px 8px", fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
                    background: r.status === "DRASigned" ? "#d1fae5" : r.status === "Draft" ? "#fef3c7" : r.status === "Superseded" ? "#f1f5f9" : "#dbeafe",
                    color: r.status === "DRASigned" ? "#065f46" : r.status === "Draft" ? "#92400e" : r.status === "Superseded" ? "#64748b" : "#1e40af",
                    borderRadius: 2,
                  }}>{r.status.toUpperCase()}</span>
                </td>
                <td style={{ fontSize: 12 }}>{r.prepared_by?.name || "—"}</td>
                <td style={{ fontSize: 12 }}>{r.stage1_sign?.operative_name || "—"}</td>
                <td style={{ fontSize: 12 }}>{r.stage2_review?.name || "—"}</td>
                <td style={{ fontSize: 12 }}>{r.dra_sign?.by_name || "—"}</td>
                <td className="mono" style={{ fontSize: 11, color: "#64748b" }}>{(r.created_at || "").slice(0, 10)}</td>
                <td><Link data-testid={`open-${r.id}`} to={`/rams/${r.id}`} className="btn-ghost" style={{ padding: "3px 10px", fontSize: 12, textDecoration: "none" }}>Open</Link></td>
              </tr>
            ))}
            {filtered.length === 0 ? <tr><td colSpan={10} style={{ textAlign: "center", color: "#94a3b8", padding: 18 }}>No RAMS in this view.</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function RAMS() {
  const [lib, setLib] = useState(null);
  const [tab, setTab] = useState("intake");
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/rams/library")
      .then((r) => setLib(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);

  return (
    <div data-testid="rams-page" style={{ padding: "24px 24px 64px" }}>
      <header style={{ marginBottom: 18 }}>
        <div className="mono" style={{ color: "var(--brand-accent, #F05A14)", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>COMPLIANCE</div>
        <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0" }}>
          <ShieldAlert size={26} style={{ verticalAlign: -3, marginRight: 8 }} />
          RAMS — Generator &amp; Register
        </h1>
        <div style={{ color: "#64748b", marginTop: 4, maxWidth: 800 }}>
          Hazards drawn from a verified library — control text is never invented per job.
          Generate a RAMS from an intake, then route it through Stage 1 (on-site authorisation), Stage 2 (competent-person review), and DRA sign-off before the structure can be Erected.
        </div>
      </header>

      {err ? <div className="badge badge-red" style={{ padding: "8px 12px", marginBottom: 12 }} data-testid="rams-error">{String(err)}</div> : null}

      {!lib ? (
        <div data-testid="rams-loading" className="mono" style={{ color: "#64748b" }}>Loading library…</div>
      ) : (
        <>
          <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0", marginBottom: 16, gap: 4 }} data-testid="rams-tabs">
            <button
              data-testid="rams-tab-intake"
              onClick={() => setTab("intake")}
              className="btn-ghost"
              style={{ border: "none", borderRadius: 0, borderBottom: tab === "intake" ? "3px solid var(--brand-accent, #F05A14)" : "3px solid transparent", padding: "10px 14px", fontWeight: 700, color: tab === "intake" ? "#0f172a" : "#64748b", background: "transparent" }}
            >
              <Sliders size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
              New RAMS
            </button>
            <button
              data-testid="rams-tab-register"
              onClick={() => setTab("register")}
              className="btn-ghost"
              style={{ border: "none", borderRadius: 0, borderBottom: tab === "register" ? "3px solid var(--brand-accent, #F05A14)" : "3px solid transparent", padding: "10px 14px", fontWeight: 700, color: tab === "register" ? "#0f172a" : "#64748b", background: "transparent" }}
            >
              <Folder size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
              Register
            </button>
            <button
              data-testid="rams-tab-library"
              onClick={() => setTab("library")}
              className="btn-ghost"
              style={{ border: "none", borderRadius: 0, borderBottom: tab === "library" ? "3px solid var(--brand-accent, #F05A14)" : "3px solid transparent", padding: "10px 14px", fontWeight: 700, color: tab === "library" ? "#0f172a" : "#64748b", background: "transparent" }}
            >
              <BookOpen size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
              Hazard library ({lib.hazards?.length || 0})
            </button>
          </div>

          {tab === "library" ? <LibraryViewer lib={lib} /> : tab === "register" ? <Register /> : <IntakePreview lib={lib} />}
        </>
      )}
    </div>
  );
}
